/**
 * CRM Deals — list view + kanban board with skeleton loading and create dialog.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Select, Badge, Tag, EmptyState, Button, SkeletonRow, Pagination } from '../../components/ui';
import { DollarSign, Plus, List, LayoutGrid } from '../../lib/icons';
import { useT } from '../../lib/i18n';
import { formatDate } from '../../lib/utils';
import { DEAL_STAGE_TONE } from './deal-format';
import { usePersistedPageSize } from '../../hooks/use-persisted-page-size';
import { fetchDeals } from '../../lib/crm-api';
import type { CrmDeal } from '../../lib/crm-api';
import { DealFormDialog } from './forms';

const STAGE_ORDER = ['draft', 'sent', 'negotiating', 'won', 'lost'];

export function DealsPage() {
  const t = useT();

  const STAGE_LABELS: Record<string, string> = {
    draft: t('crm.stageDraft'),
    sent: t('crm.stageSent'),
    negotiating: t('crm.stageNegotiating'),
    won: t('crm.stageWon'),
    lost: t('crm.stageLost'),
    cancelled: t('crm.stageCancelled'),
  };
  const [deals, setDeals] = useState<CrmDeal[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = usePersistedPageSize('crm.deals', 20);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>(() => (window.innerWidth < 768 ? 'list' : 'list'));
  const [formOpen, setFormOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const limit = viewMode === 'kanban' ? 200 : pageSize;
      const offset = viewMode === 'kanban' ? 0 : page * pageSize;
      const res = await fetchDeals({
        stage: stage || undefined,
        type: type || undefined,
        limit,
        offset,
      });
      setDeals(res.items);
      setTotal(res.total);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [stage, type, page, viewMode, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useEffect(() => {
    setPage(0);
  }, [stage, type, pageSize]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Filter bar */}
      <div className="border-b border-edge bg-surface-raised px-4 py-2.5 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Select size="sm" inline value={stage} onChange={(e) => setStage(e.target.value)}>
            <option value="">{t('crm.allStages')}</option>
            {STAGE_ORDER.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABELS[s]}
              </option>
            ))}
          </Select>
          <Select size="sm" inline value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">{t('crm.allTypes')}</option>
            <option value="quotation">{t('crm.typeQuotation')}</option>
            <option value="sales_order">{t('crm.typeSalesOrder')}</option>
          </Select>
          <div className="flex items-center border border-edge rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 transition-colors ${viewMode === 'list' ? 'bg-primary-subtle text-primary-fg-strong' : 'text-fg-muted hover:text-fg-secondary'}`}
              title={t('crm.listView')}
            >
              <List size={14} />
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 transition-colors ${viewMode === 'kanban' ? 'bg-primary-subtle text-primary-fg-strong' : 'text-fg-muted hover:text-fg-secondary'}`}
              title={t('crm.kanbanView')}
            >
              <LayoutGrid size={14} />
            </button>
          </div>
          <div className="flex-1" />
          <span className="text-xs text-fg-faint">{total} results</span>
          <Button size="sm" onClick={() => setFormOpen(true)}>
            <Plus size={14} />
            <span className="hidden sm:inline ml-1">New Deal</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {!loading && deals.length === 0 ? (
          <EmptyState
            icon={DollarSign}
            title={t('crm.noDealsFound')}
            description={stage || type ? t('crm.adjustFilters') : t('crm.createFirstDeal')}
          />
        ) : viewMode === 'kanban' ? (
          loading ? (
            <div className="flex gap-3 p-4">
              {STAGE_ORDER.map((s) => (
                <div key={s} className="flex-shrink-0 w-64 h-40 bg-surface-muted rounded-lg animate-skeleton" />
              ))}
            </div>
          ) : (
            <KanbanView deals={deals} stageLabels={STAGE_LABELS} t={t} />
          )
        ) : (
          <ListView deals={deals} loading={loading} stageLabels={STAGE_LABELS} t={t} />
        )}
      </div>

      {/* Pagination (list mode only) */}
      {viewMode === 'list' && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* Create dialog */}
      <DealFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={loadData} />
    </div>
  );
}

// ─── List View ───────────────────────────────────────────

function ListView({
  deals,
  loading,
  stageLabels,
  t,
}: {
  deals: CrmDeal[];
  loading: boolean;
  stageLabels: Record<string, string>;
  t: ReturnType<typeof useT>;
}) {
  return (
    <table className="w-full min-w-[700px] [&_td]:whitespace-nowrap">
      <thead className="sticky top-0 z-10 bg-surface-muted [&_th]:whitespace-nowrap">
        <tr className="border-b border-edge bg-surface-muted">
          <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">Number</th>
          <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">Title</th>
          <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden md:table-cell">Customer</th>
          <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">Stage</th>
          <th className="text-right text-xs font-medium text-fg-muted px-4 py-2">Amount</th>
          <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden lg:table-cell">Type</th>
          <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden lg:table-cell">Created</th>
        </tr>
      </thead>
      <tbody>
        {loading
          ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={7} />)
          : deals.map((d) => (
              <tr
                key={d.id}
                onClick={() => (window.location.hash = `#/crm/deals/${d.id}`)}
                className="border-b border-edge hover:bg-surface-muted cursor-pointer transition-colors"
              >
                <td className="px-4 py-2.5">
                  <span className="text-xs font-mono text-fg-muted">{d.deal_number}</span>
                </td>
                <td className="px-4 py-2.5">
                  <span className="block max-w-[240px] truncate text-sm font-medium text-fg" title={d.title}>
                    {d.title}
                  </span>
                </td>
                <td className="px-4 py-2.5 hidden md:table-cell">
                  <span
                    className="block max-w-[160px] truncate text-xs text-fg-secondary"
                    title={d.company_name || d.contact_name || ''}
                  >
                    {d.company_name || d.contact_name || '—'}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <Tag tone={DEAL_STAGE_TONE[d.stage] ?? 'neutral'} truncate>
                    {stageLabels[d.stage] || d.stage}
                  </Tag>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <span className="text-sm font-semibold text-fg">
                    {d.currency === 'USD' ? '$' : ''}
                    {(d.amount || 0).toLocaleString()}
                  </span>
                  <span className="text-[10px] text-fg-faint ml-1">{d.currency}</span>
                </td>
                <td className="px-4 py-2.5 hidden lg:table-cell">
                  <Badge>{d.type === 'quotation' ? t('crm.typeQuoteShort') : t('crm.typeOrderShort')}</Badge>
                </td>
                <td className="px-4 py-2.5 hidden lg:table-cell">
                  <span className="text-[11px] text-fg-muted">{formatDate(d.created_at)}</span>
                </td>
              </tr>
            ))}
      </tbody>
    </table>
  );
}

// ─── Kanban View ─────────────────────────────────────────

function KanbanView({
  deals,
  stageLabels,
  t,
}: {
  deals: CrmDeal[];
  stageLabels: Record<string, string>;
  t: ReturnType<typeof useT>;
}) {
  const columns = STAGE_ORDER.map((stg) => ({
    stage: stg,
    label: stageLabels[stg],
    deals: deals.filter((d) => d.stage === stg),
    totalAmount: deals.filter((d) => d.stage === stg).reduce((sum, d) => sum + (d.amount || 0), 0),
  }));

  return (
    <div className="flex gap-3 p-4 overflow-x-auto h-full">
      {columns.map((col) => (
        <div key={col.stage} className="flex-shrink-0 w-64 flex flex-col">
          <div className="flex items-center justify-between px-2 py-2 mb-2">
            <div className="flex items-center gap-2">
              <Tag tone={DEAL_STAGE_TONE[col.stage] ?? 'neutral'}>{col.label}</Tag>
              <span className="text-xs text-fg-faint">{col.deals.length}</span>
            </div>
            <span className="text-[10px] text-fg-muted font-mono">${col.totalAmount.toLocaleString()}</span>
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto pb-4">
            {col.deals.map((d) => (
              <Card
                key={d.id}
                className="p-3 cursor-pointer hover:bg-surface-muted transition-colors"
                onClick={() => (window.location.hash = `#/crm/deals/${d.id}`)}
              >
                <div className="text-xs font-mono text-fg-muted mb-1">{d.deal_number}</div>
                <div className="text-sm font-medium text-fg truncate mb-1" title={d.title}>
                  {d.title}
                </div>
                {(d.company_name || d.contact_name) && (
                  <div className="text-[11px] text-fg-muted truncate mb-2">{d.company_name || d.contact_name}</div>
                )}
                <div className="flex items-center justify-between">
                  <Badge>{d.type === 'quotation' ? t('crm.typeQuoteShort') : t('crm.typeOrderShort')}</Badge>
                  <span className="text-sm font-semibold text-fg">${(d.amount || 0).toLocaleString()}</span>
                </div>
              </Card>
            ))}
            {col.deals.length === 0 && <div className="text-center text-xs text-fg-faint py-8">No deals</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
